<script setup>
import {
  ref,
  reactive,
  computed,
  watch,
  onBeforeUnmount,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useDebounceFn } from '@vueuse/core';
import {
  useSnakeCase,
  useCamelCase,
} from 'dashboard/composables/useTransformKeys';
import { useContactFilterContext } from 'dashboard/components-next/filter/contactProvider';
import { useConversationFilterContext } from 'dashboard/components-next/filter/provider';
import { useOperators } from 'dashboard/components-next/filter/operators';
import ContactsAPI from 'dashboard/api/contacts';

import Button from 'dashboard/components-next/button/Button.vue';
import FilterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';

import CampaignsAPI from 'dashboard/api/campaigns';

const props = defineProps({
  inboxId: {
    type: Number,
    default: null,
  },
  initialAudience: {
    type: Object,
    default: () => ({
      contacts: [],
      labels: [],
      contactFilters: [],
      conversationFilters: [],
    }),
  },
});

const emit = defineEmits([
  'update:audience',
  'update:estimate',
  'update:actions',
]);

const { t } = useI18n();
const isDevMode =
  (import.meta?.env?.DEV ?? false) ||
  (typeof window !== 'undefined' && window.location?.hostname === 'localhost');
const logDebug = (...args) => {
  if (isDevMode) {
    // eslint-disable-next-line no-console
    console.debug('[AudienceBuilder]', ...args);
  }
};

// Filter contexts (reuse automation-style condition row)
const { filterTypes: contactFilterTypes } = useContactFilterContext();
const { filterTypes: conversationFilterTypes } = useConversationFilterContext();
const { equalityOperators } = useOperators();

const allowedContactKeys = new Set(['created_at', 'last_activity_at']);
const allowedConversationKeys = new Set([
  'status',
  'assignee_id',
  'inbox_id',
  'priority',
]);

const labelsStore = useMapGetter('labels/getLabels');

const labelOptions = computed(
  () =>
    labelsStore.value?.map(label => ({
      id: label.id,
      name: label.title,
      value: label.id,
      label: label.title,
    })) ?? []
);

const filteredContactTypes = computed(() =>
  contactFilterTypes.value
    .filter(
      filter =>
        allowedContactKeys.has(filter.attributeKey) ||
        filter.attributeModel === 'customAttributes'
    )
    .map(filter => ({ ...filter, resource: 'contact' }))
);

const filteredConversationTypes = computed(() =>
  conversationFilterTypes.value
    .filter(
      filter =>
        allowedConversationKeys.has(filter.attributeKey) ||
        filter.attributeModel === 'customAttributes'
    )
    .map(filter => {
      const baseFilter =
        filter.attributeKey === 'inbox_id'
          ? {
              ...filter,
              filterOperators: equalityOperators.value,
            }
          : filter;

      return { ...baseFilter, resource: 'conversation' };
    })
);

const labelConditionType = computed(() => {
  const operator = equalityOperators.value?.[0] || {
    value: 'equal_to',
    label: t('FILTER.OPERATORS.EQUAL_TO'),
    icon: '',
    hasInput: true,
  };

  return {
    attributeKey: 'labels',
    value: 'labels',
    attributeName: t('CAMPAIGN.AUDIENCE_BUILDER.LABELS.LABEL'),
    label: t('CAMPAIGN.AUDIENCE_BUILDER.LABELS.LABEL'),
    inputType: 'multiSelect',
    options: labelOptions.value.map(option => ({
      id: option.id,
      name: option.label,
    })),
    dataType: 'text',
    filterOperators: [operator],
    attributeModel: 'standard',
    resource: 'label',
  };
});

const contactSelectType = computed(() => {
  const operator = equalityOperators.value?.[0] || {
    value: 'equal_to',
    label: t('FILTER.OPERATORS.EQUAL_TO'),
    icon: '',
    hasInput: true,
  };

  return {
    attributeKey: 'contact_select',
    value: 'contact_select',
    attributeName: t('CAMPAIGN.AUDIENCE_BUILDER.CONTACTS.TITLE'),
    label: t('CAMPAIGN.AUDIENCE_BUILDER.CONTACTS.TITLE'),
    inputType: 'contactSelect',
    options: [],
    dataType: 'text',
    filterOperators: [operator],
    attributeModel: 'standard',
    resource: 'contact',
  };
});

const combinedFilterTypes = computed(() => [
  ...filteredContactTypes.value,
  ...filteredConversationTypes.value,
  labelConditionType.value,
  contactSelectType.value,
]);

// Contact search (for contact_select condition)
const contactOptions = ref([]);
const isContactSearchLoading = ref(false);
let contactSearchAbortController = null;

const formatContactOption = contact => {
  const phone = contact.phone_number || contact.phoneNumber;
  const label =
    phone && contact.name
      ? `${contact.name} (${phone})`
      : contact.name || contact.id;
  return { value: contact.id, label };
};

const upsertContactOptions = contacts => {
  const map = new Map(
    contactOptions.value.map(option => [option.value, option])
  );
  contacts.forEach(contact => {
    const option = formatContactOption(contact);
    map.set(option.value, option);
  });
  contactOptions.value = Array.from(map.values());
};

const fetchContacts = async searchTerm => {
  if (contactSearchAbortController) {
    contactSearchAbortController.abort();
  }
  contactSearchAbortController = new AbortController();
  isContactSearchLoading.value = true;
  try {
    const response = await ContactsAPI.search(searchTerm, 1, 'name', '', {
      signal: contactSearchAbortController.signal,
    });
    const contacts =
      response.data?.payload ||
      response.data?.data ||
      response.data?.records ||
      [];
    upsertContactOptions(contacts);
  } catch (error) {
    if (error.name === 'AbortError') return;
  } finally {
    isContactSearchLoading.value = false;
    contactSearchAbortController = null;
  }
};

const debouncedFetchContacts = useDebounceFn(fetchContacts, 300);

const hydrateContacts = async ids => {
  if (!ids || !ids.length) return;
  const missingIds = ids.filter(
    id => !contactOptions.value.some(option => option.value === id)
  );

  await Promise.all(
    missingIds.map(async id => {
      try {
        const response = await ContactsAPI.show(id);
        if (response.data) {
          const payload = response.data.payload || response.data;
          const contact = payload.contact || payload;
          upsertContactOptions([contact]);
        }
      } catch (error) {
        // ignore failures; fallback will display raw id
      }
    })
  );
};

const toFilterAttributes = computed(() =>
  combinedFilterTypes.value.map(filter => ({
    key: filter.attributeKey,
    name: filter.attributeName || filter.label,
    attributeType: filter.attributeModel,
  }))
);

const getFilterByKey = key =>
  combinedFilterTypes.value.find(filter => filter.attributeKey === key) ??
  combinedFilterTypes.value[0];

const defaultValueForFilter = filter => {
  if (filter.inputType === 'multiSelect') return [];
  if (['searchSelect', 'booleanSelect'].includes(filter.inputType)) return {};
  if (filter.inputType === 'date') return '';
  if (filter.inputType === 'contactSelect') return [];
  return '';
};

const getInputTypeFor = (attributeKey, operator) => {
  const filter = getFilterByKey(attributeKey);
  if (!filter) return 'text';
  // [PROSPEK] 2026-06-20 Relative day audience filters require numeric input values
  if (['days_before', 'days_after'].includes(operator)) return 'number';
  // [/PROSPEK]
  if (filter.attributeKey === 'labels') return 'multi_select';
  if (filter.attributeKey === 'contact_select') return 'contact_select';
  if (filter.inputType === 'multiSelect') return 'multi_select';
  if (filter.inputType === 'searchSelect') return 'search_select';
  if (filter.inputType === 'booleanSelect') return 'boolean';
  if (filter.inputType === 'date') return 'date';
  return 'text';
};

const normalizeOptions = options =>
  options?.map(opt => {
    let id = opt;
    if (opt?.id !== undefined) {
      id = opt.id;
    } else if (opt?.value !== undefined) {
      id = opt.value;
    }

    let name = String(opt);
    if (opt?.name !== undefined) {
      name = opt.name;
    } else if (opt?.label !== undefined) {
      name = opt.label;
    }

    return { id, name };
  }) ?? [];

const getDropdownValues = attributeKey => {
  const filter = getFilterByKey(attributeKey);
  if (!filter) return [];
  if (filter.attributeKey === 'labels')
    return normalizeOptions(labelOptions.value);
  if (filter.attributeKey === 'contact_select') return contactOptions.value;
  return normalizeOptions(filter.options);
};

// [PROSPEK] 2026-06-21 render checkbox custom attributes as value-only Checked/Unchecked selectors
const isCheckboxFilter = attributeKey => {
  const filter = getFilterByKey(attributeKey);
  return filter?.attributeDisplayType === 'checkbox';
};

const getOperatorsFor = attributeKey => {
  const filter = getFilterByKey(attributeKey);
  if (!filter) return [];
  const operators = filter.filterOperators?.map(operator => ({
    value: operator.value,
    label: operator.label || operator.value,
  }));

  return isCheckboxFilter(attributeKey) ? operators?.slice(0, 1) : operators;
};
// [/PROSPEK]

const getAttributeModel = attributeKey => {
  const filter = getFilterByKey(attributeKey);
  return filter?.attributeModel || '';
};

let conditionId = 0;
const buildCondition = filter => {
  conditionId += 1;
  return {
    id: `condition-${conditionId}`,
    model: {
      attribute_key: filter.attributeKey,
      filter_operator: filter.filterOperators[0]?.value || 'equal_to',
      values: defaultValueForFilter(filter),
      query_operator: 'and',
      custom_attribute_type: filter.attributeModel,
    },
  };
};

const conditions = ref([buildCondition(labelConditionType.value)]);

const syncQueryOperators = () => {
  conditions.value.forEach((condition, index) => {
    condition.model.query_operator =
      index === conditions.value.length - 1 ? 'and' : 'and';
  });
};

const addCondition = () => {
  const baseFilter = labelConditionType.value;
  if (conditions.value.length) {
    const lastIndex = conditions.value.length - 1;
    conditions.value[lastIndex].model.query_operator = 'and';
  }
  const newCondition = buildCondition(baseFilter);
  conditions.value.push(newCondition);
  syncQueryOperators();
};

const removeCondition = index => {
  conditions.value.splice(index, 1);
  if (!conditions.value.length) {
    conditions.value.push(
      buildCondition(combinedFilterTypes.value[0] || labelConditionType.value)
    );
  }
  syncQueryOperators();
};

const resetCondition = index => {
  const condition = conditions.value[index];
  const filter = getFilterByKey(condition.model.attribute_key);
  condition.model.filter_operator =
    filter.filterOperators[0]?.value || 'equal_to';
  condition.model.values = defaultValueForFilter(filter);
  condition.model.query_operator = 'and';
  // [PROSPEK] 2026-06-21 clear hydrated checkbox payload preservation when a condition changes
  delete condition.model.preserve_exact_false_filter;
  delete condition.model.preserve_exact_not_false_filter;
  // [/PROSPEK]
};

const normalizeValue = value => {
  if (Array.isArray(value)) {
    return value
      .map(val => {
        if (val?.id !== undefined) return val.id;
        if (val?.value !== undefined) return val.value;
        return val;
      })
      .filter(val => val !== undefined && val !== null && val !== '');
  }

  if (value && typeof value === 'object') {
    if ('id' in value) return value.id;
    if ('value' in value) return value.value;
    if ('name' in value) return value.name;
  }

  return value;
};

// [PROSPEK] 2026-06-21 preserve legacy checkbox custom-attribute payload semantics behind one-column UI
const checkboxDisplayValues = filter => {
  const values = filter.values || [];
  const firstValue = normalizeValue(values[0]);

  if (
    filter.filterOperator === 'not_equal_to' &&
    typeof firstValue === 'boolean'
  ) {
    return [!firstValue];
  }

  return values;
};

const isExactFalseCheckboxFilter = filter =>
  filter.filterOperator === 'equal_to' &&
  normalizeValue(filter.values?.[0]) === false;

const isExactNotFalseCheckboxFilter = filter =>
  filter.filterOperator === 'not_equal_to' &&
  normalizeValue(filter.values?.[0]) === false;

const checkboxFilterPayload = (model, valuesArray) => {
  if (!isCheckboxFilter(model.attribute_key)) {
    return {
      filterOperator: model.filter_operator,
      values: valuesArray,
    };
  }

  const checked = valuesArray[0] === true;
  if (![true, false].includes(valuesArray[0])) {
    return {
      filterOperator: 'equal_to',
      values: [],
    };
  }

  if (model.preserve_exact_false_filter && valuesArray[0] === false) {
    return {
      filterOperator: 'equal_to',
      values: [false],
    };
  }

  if (model.preserve_exact_not_false_filter && valuesArray[0] === true) {
    return {
      filterOperator: 'not_equal_to',
      values: [false],
    };
  }

  return {
    filterOperator: checked ? 'equal_to' : 'not_equal_to',
    values: [true],
  };
};
// [/PROSPEK]

const buildFiltersFromConditions = () => {
  const contactFilters = [];
  const conversationFilters = [];
  const labelValues = new Set();
  let contactIds = [];

  conditions.value.forEach(condition => {
    const model = condition.model;
    const filter = getFilterByKey(model.attribute_key);
    const normalizedValues = normalizeValue(model.values);
    const queryOperator = 'and';

    if (filter.attributeKey === 'contact_select') {
      const ids = Array.isArray(normalizedValues)
        ? normalizedValues
        : [normalizedValues].filter(
            v => v !== undefined && v !== null && v !== ''
          );
      contactIds = ids;
      return;
    }

    if (filter.attributeKey === 'labels') {
      (Array.isArray(normalizedValues)
        ? normalizedValues
        : [normalizedValues]
      ).forEach(val => {
        if (val !== undefined && val !== null && val !== '') {
          labelValues.add(val);
        }
      });
      return;
    }

    const targetFilters =
      filter.resource === 'conversation' ? conversationFilters : contactFilters;

    let valuesArray = [];
    if (Array.isArray(normalizedValues)) {
      valuesArray = normalizedValues;
    } else if (normalizedValues !== undefined && normalizedValues !== null) {
      valuesArray = [normalizedValues];
    }

    // Drop blank/empty values to avoid invalid queries (e.g., empty dates)
    valuesArray = valuesArray.filter(
      val => val !== '' && val !== null && val !== undefined
    );

    // Only add filter if it has values OR is a presence check operator
    // [PROSPEK] 2026-06-21 translate checkbox custom-attribute UI values into backend-compatible payloads
    const filterPayload = checkboxFilterPayload(model, valuesArray);
    const filterOperator = filterPayload.filterOperator;
    const isPresenceCheck = ['is_present', 'is_not_present'].includes(
      filterOperator
    );
    const hasValues = filterPayload.values.length > 0;

    if (hasValues || isPresenceCheck) {
      targetFilters.push(
        useSnakeCase({
          attributeKey: filter.attributeKey,
          filterOperator,
          values: filterPayload.values,
          queryOperator,
        })
      );
    }
    // [/PROSPEK]
  });

  return {
    contactIds,
    contactFilters,
    conversationFilters,
    labels: Array.from(labelValues),
  };
};

const estimate = reactive({
  total: 0,
  isLoading: false,
  error: null,
  lastGoodTotal: 0,
  thresholds: null,
  isCurrentSuccess: false,
});

// [PROSPEK] 2026-08-03 Keep live threshold warnings scoped to the current audience estimate
let estimateAbortController = null;
let estimateTimer = null;
let estimateRequestSequence = 0;

const isNonNegativeInteger = value =>
  Number.isFinite(value) && Number.isInteger(value) && value >= 0;

const parseThresholds = thresholds => {
  if (
    !isNonNegativeInteger(thresholds?.soft) ||
    !isNonNegativeInteger(thresholds?.hard) ||
    thresholds.soft > thresholds.hard
  ) {
    return null;
  }

  return { soft: thresholds.soft, hard: thresholds.hard };
};

const emitEstimate = () => {
  emit('update:estimate', {
    total: estimate.total,
    isLoading: estimate.isLoading,
    error: estimate.error,
  });
};

const updateEstimate = updates => {
  Object.assign(estimate, updates);
  emitEstimate();
};

const invalidateEstimate = () => {
  estimateRequestSequence += 1;
  if (estimateTimer !== null) {
    clearTimeout(estimateTimer);
    estimateTimer = null;
  }
  if (estimateAbortController) {
    estimateAbortController.abort();
    estimateAbortController = null;
  }

  updateEstimate({
    total: estimate.lastGoodTotal,
    thresholds: null,
    isCurrentSuccess: false,
    error: null,
    isLoading: false,
  });
};
// [/PROSPEK]

// Actions after sending (post-send labeling)
let actionId = 0;
const buildAction = () => {
  actionId += 1;
  return {
    id: `action-${actionId}`,
    action_name: 'add_label',
    action_params: [],
  };
};

const actions = ref([buildAction()]);

const addAction = () => {
  actions.value.push(buildAction());
};

const removeAction = index => {
  actions.value.splice(index, 1);
};

const audiencePayload = computed(() => {
  const { contactIds, contactFilters, conversationFilters, labels } =
    buildFiltersFromConditions();

  return {
    contacts: contactIds,
    labels,
    contact_filters: contactFilters,
    conversation_filters: conversationFilters,
  };
});

// [PROSPEK] 2025-11-27 Shared audience-source check to determine if estimate should run
const hasAudienceSource = payload =>
  payload.contacts.length > 0 ||
  payload.labels.length > 0 ||
  payload.contact_filters.length > 0 ||
  payload.conversation_filters.length > 0;
// [/PROSPEK]

// [PROSPEK] 2026-08-03 Render warnings only from valid live inbox thresholds
const hasValidAudienceThresholds = computed(
  () =>
    estimate.thresholds !== null &&
    isNonNegativeInteger(estimate.thresholds.soft) &&
    isNonNegativeInteger(estimate.thresholds.hard)
);

const showHardAudienceWarning = computed(
  () =>
    hasValidAudienceThresholds.value &&
    estimate.isCurrentSuccess &&
    estimate.total >= estimate.thresholds.hard
);

const showSoftAudienceWarning = computed(
  () =>
    hasValidAudienceThresholds.value &&
    estimate.isCurrentSuccess &&
    estimate.total >= estimate.thresholds.soft &&
    estimate.total < estimate.thresholds.hard
);
// [/PROSPEK]

const actualAudienceNote = computed(
  () => `(${t('CAMPAIGN.AUDIENCE_BUILDER.ESTIMATE.ACTUAL_AUDIENCE_NOTE')})`
);

// [PROSPEK] 2026-08-03 Discard stale estimate responses, preserve valid totals, and sanitize errors
const fetchEstimate = async (requestSequence, audience, inboxId) => {
  if (requestSequence !== estimateRequestSequence) return;

  const controller = new AbortController();
  estimateAbortController = controller;
  updateEstimate({ isLoading: true, error: null });
  logDebug('fetchEstimate payload', audiencePayload.value);

  try {
    const response = await CampaignsAPI.estimateAudience(
      {
        audience,
        inbox_id: inboxId,
      },
      { signal: controller.signal }
    );

    if (requestSequence !== estimateRequestSequence) return;
    const total = response.data?.total;
    const hasValidTotal = isNonNegativeInteger(total);
    updateEstimate({
      total: hasValidTotal ? total : 0,
      lastGoodTotal: hasValidTotal ? total : 0,
      thresholds: parseThresholds(response.data?.thresholds),
      isCurrentSuccess: hasValidTotal,
      error: null,
      isLoading: false,
    });
  } catch (error) {
    if (requestSequence !== estimateRequestSequence) return;
    if (error.name === 'AbortError') {
      updateEstimate({
        total: 0,
        lastGoodTotal: 0,
        thresholds: null,
        isCurrentSuccess: false,
        error: null,
        isLoading: false,
      });
      return;
    }

    if (isDevMode) {
      // eslint-disable-next-line no-console
      console.error('Estimate fetch error:', error);
    }
    if (requestSequence !== estimateRequestSequence) return;
    updateEstimate({
      total: estimate.lastGoodTotal,
      thresholds: null,
      isCurrentSuccess: false,
      error: t('CAMPAIGN.AUDIENCE_BUILDER.ESTIMATE.ERROR'),
      isLoading: false,
    });
  } finally {
    if (
      requestSequence === estimateRequestSequence &&
      estimateAbortController === controller
    ) {
      estimateAbortController = null;
    }
  }
};

const scheduleEstimate = () => {
  const requestSequence = estimateRequestSequence;
  const audience = audiencePayload.value;
  const inboxId = props.inboxId;
  estimateTimer = setTimeout(() => {
    estimateTimer = null;
    if (requestSequence !== estimateRequestSequence) return;
    fetchEstimate(requestSequence, audience, inboxId);
  }, 500);
};

const retryEstimate = () => {
  invalidateEstimate();
  if (props.inboxId && hasAudienceSource(audiencePayload.value)) {
    fetchEstimate(
      estimateRequestSequence,
      audiencePayload.value,
      props.inboxId
    );
  }
};
// [/PROSPEK]

// [PROSPEK] 2025-11-27 Re-run estimate when audience payload changes AND both inbox and audience sources exist
watch(
  audiencePayload,
  newPayload => {
    if (isDevMode && typeof window !== 'undefined') {
      window.audiencePayloadSnapshot = JSON.parse(
        JSON.stringify(newPayload ?? {})
      );
    }

    invalidateEstimate();
    if (props.inboxId && hasAudienceSource(newPayload)) {
      scheduleEstimate();
    }

    emit('update:audience', newPayload);
  },
  { deep: true }
);
// [/PROSPEK]

// [PROSPEK] 2025-11-27 Re-run estimate when inbox is selected after filters/labels are already set
watch(
  () => props.inboxId,
  newInboxId => {
    invalidateEstimate();
    if (newInboxId && hasAudienceSource(audiencePayload.value)) {
      scheduleEstimate();
    }
  }
);
// [/PROSPEK]

// [PROSPEK] 2025-11-27 Hydrate audience from initialAudience prop for campaign editing
const hydrateConditionsFromAudience = audience => {
  if (!audience) return [buildCondition(combinedFilterTypes.value[0])];

  const hydratedConditions = [];
  const contactIds = audience.contacts || [];

  // Convert snake_case filters from backend to camelCase
  const contactFilters = (audience.contact_filters || []).map(f =>
    useCamelCase(f)
  );
  const conversationFilters = (audience.conversation_filters || []).map(f =>
    useCamelCase(f)
  );
  const labels = audience.labels || [];

  if (contactIds.length > 0) {
    const condition = buildCondition(contactSelectType.value);
    condition.model.attribute_key = 'contact_select';
    condition.model.filter_operator = 'equal_to';
    condition.model.values = contactIds;
    hydratedConditions.push(condition);
    hydrateContacts(contactIds);
  }

  // Hydrate label conditions
  labels.forEach(labelId => {
    const condition = buildCondition(labelConditionType.value);
    condition.model.attribute_key = 'labels';
    condition.model.filter_operator = 'equal_to';
    condition.model.values = [labelId];
    hydratedConditions.push(condition);
  });

  // Hydrate contact filter conditions
  contactFilters.forEach(filter => {
    const filterType = getFilterByKey(filter.attributeKey);
    if (!filterType) {
      logDebug('Cannot hydrate unknown contact filter:', filter.attributeKey);
      return;
    }
    const condition = buildCondition(filterType);
    condition.model.attribute_key = filter.attributeKey;
    condition.model.filter_operator = filter.filterOperator || 'equal_to';
    // [PROSPEK] 2026-06-21 preserve checkbox custom-attribute legacy payload semantics during hydration
    condition.model.values = isCheckboxFilter(filter.attributeKey)
      ? checkboxDisplayValues(filter)
      : filter.values || [];
    condition.model.preserve_exact_false_filter =
      isCheckboxFilter(filter.attributeKey) &&
      isExactFalseCheckboxFilter(filter);
    condition.model.preserve_exact_not_false_filter =
      isCheckboxFilter(filter.attributeKey) &&
      isExactNotFalseCheckboxFilter(filter);
    // [/PROSPEK]
    condition.model.query_operator = 'and';
    condition.model.custom_attribute_type = filterType.attributeModel;
    hydratedConditions.push(condition);
  });

  // Hydrate conversation filter conditions
  conversationFilters.forEach(filter => {
    const filterType = getFilterByKey(filter.attributeKey);
    if (!filterType) {
      logDebug(
        'Cannot hydrate unknown conversation filter:',
        filter.attributeKey
      );
      return;
    }
    const condition = buildCondition(filterType);
    condition.model.attribute_key = filter.attributeKey;
    condition.model.filter_operator = filter.filterOperator || 'equal_to';
    // [PROSPEK] 2026-06-21 preserve checkbox custom-attribute legacy payload semantics during hydration
    condition.model.values = isCheckboxFilter(filter.attributeKey)
      ? checkboxDisplayValues(filter)
      : filter.values || [];
    condition.model.preserve_exact_false_filter =
      isCheckboxFilter(filter.attributeKey) &&
      isExactFalseCheckboxFilter(filter);
    condition.model.preserve_exact_not_false_filter =
      isCheckboxFilter(filter.attributeKey) &&
      isExactNotFalseCheckboxFilter(filter);
    // [/PROSPEK]
    condition.model.query_operator = 'and';
    condition.model.custom_attribute_type = filterType.attributeModel;
    hydratedConditions.push(condition);
  });

  // Return default condition if nothing was hydrated
  return hydratedConditions.length > 0
    ? hydratedConditions
    : [buildCondition(combinedFilterTypes.value[0])];
};

watch(
  () => props.initialAudience,
  newInitial => {
    if (!newInitial) return;
    conditions.value = hydrateConditionsFromAudience(newInitial);
    syncQueryOperators();
  },
  { immediate: true }
);
// [/PROSPEK]

onBeforeUnmount(() => {
  invalidateEstimate();
  if (contactSearchAbortController) {
    contactSearchAbortController.abort();
  }
});

onMounted(() => {
  fetchContacts('');
});

watch(
  actions,
  newActions => {
    const normalizedActions = newActions.map(action => ({
      type: action.action_name,
      labels: normalizeValue(action.action_params),
    }));
    emit('update:actions', normalizedActions);
  },
  { deep: true }
);

const actionTypeOptions = computed(() => [
  {
    key: 'add_label',
    label: t(
      'CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.CONVERSATION_LABEL.ACTION_ADD_LABEL'
    ),
    inputType: 'multi_select',
  },
]);
</script>

<template>
  <div class="space-y-8">
    <section class="space-y-2">
      <div
        class="w-full p-4 border border-solid rounded-lg bg-n-slate-2 dark:bg-n-solid-2 border-n-strong"
      >
        <!-- [PROSPEK] 2026-06-21 checkbox custom attributes use value-only rows without an operator column -->
        <FilterInputBox
          v-for="(condition, index) in conditions"
          :key="condition.id"
          v-model="condition.model"
          :filter-attributes="toFilterAttributes"
          :input-type="
            getInputTypeFor(
              condition.model.attribute_key,
              condition.model.filter_operator
            )
          "
          :operators="getOperatorsFor(condition.model.attribute_key)"
          :dropdown-values="getDropdownValues(condition.model.attribute_key)"
          :show-query-operator="false"
          :show-operator="!isCheckboxFilter(condition.model.attribute_key)"
          :custom-attribute-type="
            getAttributeModel(condition.model.attribute_key)
          "
          :contact-options="contactOptions"
          :contact-loading="isContactSearchLoading"
          :contact-placeholder="
            t('CAMPAIGN.AUDIENCE_BUILDER.CONTACTS.PLACEHOLDER')
          "
          :contact-empty-state="t('CAMPAIGN.AUDIENCE_BUILDER.CONTACTS.EMPTY')"
          contact-message=""
          :on-contact-search="debouncedFetchContacts"
          :no-options-text="
            condition.model.attribute_key === 'labels'
              ? t('CAMPAIGN.AUDIENCE_BUILDER.LABELS.EMPTY')
              : ''
          "
          @remove-filter="removeCondition(index)"
          @reset-filter="resetCondition(index)"
        />
        <!-- [/PROSPEK] -->

        <div class="mt-4">
          <Button
            type="button"
            size="sm"
            variant="faded"
            color="blue"
            icon="i-lucide-plus"
            :label="t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
            class="self-start"
            @click="addCondition"
          />
        </div>
      </div>
    </section>

    <section class="space-y-4">
      <div v-if="estimate.error" class="flex items-center gap-2">
        <span class="text-xs text-n-ruby-9">
          {{ estimate.error }}
        </span>
        <Button
          type="button"
          size="xs"
          variant="ghost"
          color="slate"
          @click="retryEstimate"
        >
          {{ t('CAMPAIGN.AUDIENCE_BUILDER.ESTIMATE.RETRY') }}
        </Button>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CAMPAIGN.AUDIENCE_BUILDER.ESTIMATE.LABEL') }}
        </span>
        <span
          v-if="estimate.isLoading"
          class="text-xs font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.AUDIENCE_BUILDER.ESTIMATE.LOADING') }}
        </span>
        <span
          v-else-if="estimate.total === 0"
          class="text-xs font-medium text-n-amber-11"
        >
          {{ t('CAMPAIGN.AUDIENCE_BUILDER.ESTIMATE.ZERO') }}
        </span>
        <span v-else class="text-xs font-medium text-n-slate-12">
          {{ estimate.total.toLocaleString() }}
          {{ t('CAMPAIGN.AUDIENCE_BUILDER.ESTIMATE.CONTACTS') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ actualAudienceNote }}
        </span>
      </div>

      <div
        class="flex flex-col gap-2 p-4 border rounded-lg bg-n-amber-alpha-2 border-n-amber-6"
      >
        <div class="flex items-start gap-2">
          <span
            class="flex-shrink-0 mt-0.5 i-lucide-alert-triangle size-4 text-n-amber-9"
          />
          <div class="flex flex-col flex-1 gap-1">
            <p class="text-sm font-medium text-n-amber-11">
              {{ t('CAMPAIGN.AUDIENCE_BUILDER.BANNER.TITLE') }}
            </p>
            <p class="text-xs text-n-slate-11 whitespace-pre-line">
              {{ t('CAMPAIGN.AUDIENCE_BUILDER.BANNER.MESSAGE') }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="showSoftAudienceWarning"
        class="flex items-start gap-2 p-3 border rounded-lg bg-n-amber-alpha-2 border-n-amber-6"
      >
        <span
          class="flex-shrink-0 mt-0.5 i-lucide-alert-circle size-4 text-n-amber-9"
        />
        <div class="flex-1">
          <p class="text-sm font-medium text-n-amber-11">
            {{ t('CAMPAIGN.AUDIENCE_BUILDER.WARNING.LARGE_AUDIENCE.TITLE') }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{
              t('CAMPAIGN.AUDIENCE_BUILDER.WARNING.LARGE_AUDIENCE.MESSAGE', {
                count: estimate.total.toLocaleString(),
              })
            }}
          </p>
        </div>
      </div>

      <div
        v-if="showHardAudienceWarning"
        class="flex items-start gap-2 p-3 border rounded-lg bg-n-ruby-alpha-2 border-n-ruby-6"
      >
        <span
          class="flex-shrink-0 mt-0.5 i-lucide-alert-circle size-4 text-n-ruby-9"
        />
        <div class="flex-1">
          <p class="text-sm font-medium text-n-ruby-11">
            {{ t('CAMPAIGN.AUDIENCE_BUILDER.WARNING.LARGE_AUDIENCE.TITLE') }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{
              t('CAMPAIGN.AUDIENCE_BUILDER.WARNING.LARGE_AUDIENCE.MESSAGE', {
                count: estimate.total.toLocaleString(),
              })
            }}
          </p>
        </div>
      </div>
    </section>

    <section class="space-y-2">
      <div class="space-y-1">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.CONVERSATION_LABEL.TOGGLE') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.CONVERSATION_LABEL.HELPER') }}
        </p>
      </div>

      <div
        class="w-full p-4 border border-solid rounded-lg bg-n-slate-2 dark:bg-n-solid-2 border-n-strong"
      >
        <div v-for="(action, index) in actions" :key="action.id" class="mb-4">
          <AutomationActionInput
            v-model="actions[index]"
            :action-types="actionTypeOptions"
            :dropdown-values="labelOptions"
            show-action-input
            :no-options-message="
              t('CAMPAIGN.AUDIENCE_BUILDER.LABELS.NO_LABELS_FOUND')
            "
            @remove-action="removeAction(index)"
          />
        </div>

        <div class="mt-4">
          <Button
            type="button"
            size="sm"
            variant="faded"
            color="blue"
            icon="i-lucide-plus"
            :label="t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
            @click="addAction"
          />
        </div>
      </div>
    </section>
  </div>
</template>
